import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, Search, Filter, ChevronDown, ChevronUp, User } from 'lucide-react';
import Table, { ActionButton, ActionMenu, StatusBadge } from '../../components/common/Table';
import Modal, { ConfirmModal, FormModal } from '../../components/common/Modal';
import apiService from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

/**
 * Users Management Page
 * 
 * Comprehensive user management interface for administrators.
 * Allows viewing, creating, updating, and deleting user accounts.
 */
const UsersPage = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
    page: 1,
    limit: 20,
  });

  // Modal states
  const [selectedUser, setSelectedUser] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'observer',
  });

  // Filter collapse state
  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(true);

  // Create admin form state
  const [createAdminData, setCreateAdminData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    farms: [],
  });

  const columns = [
    {
      key: 'name',
      title: 'Name',
      sortable: true,
      render: (user) => (
        <div className="flex items-center">
          <img
            className="w-8 h-8 rounded-full mr-3"
            src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=339f68&color=fff`}
            alt={user.name}
          />
          <div>
            <div className="font-medium text-secondary-900">{user.name}</div>
            <div className="text-sm text-secondary-500">{user.email}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      title: 'Role',
      sortable: true,
      className: 'hidden md:table-cell',
      render: (user) => {
        const roleColors = {
          admin: 'danger',
          researcher: 'warning',
          observer: 'info',
        };
        return (
          <StatusBadge
            status={user.role}
            variant={roleColors[user.role] || 'default'}
          />
        );
      },
    },
    {
      key: 'last_login_at',
      title: 'Last Login',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (user) => {
        if (!user.last_login_at) return 'Never';
        const date = new Date(user.last_login_at);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 1) return 'Today';
        if (diffDays <= 7) return `${diffDays} days ago`;
        return date.toLocaleDateString();
      },
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      className: 'hidden lg:table-cell',
      render: (user) => new Date(user.created_at).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: '120px',
      render: (user) => (
        <div className="flex space-x-2">
          <ActionButton
            icon={Eye}
            label="View Details"
            onClick={() => handleViewUser(user)}
          />
          <ActionMenu
            item={user}
            actions={[
              {
                key: 'edit',
                label: 'Edit User',
                icon: Edit,
                onClick: handleEditUser,
              },
              {
                key: 'delete',
                label: 'Delete User',
                icon: Trash2,
                variant: 'danger',
                onClick: handleDeleteUser,
              },
            ]}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    loadUsers();
    loadFarms();
  }, [filters, sortConfig]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        sort_by: sortConfig.key,
        sort_direction: sortConfig.direction,
      };

      const response = await apiService.getUsers(params);
      
      if (response.success) {
        setUsers(response.data.users || []);
        setPagination(response.data.pagination);
      } else {
        toast.error(response.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadFarms = async () => {
    try {
      const response = await apiService.getFarms({ limit: 1000 });
      if (response.success) {
        setFarms(response.data.farms || []);
      }
    } catch (error) {
      console.error('Error loading farms:', error);
    }
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
    });
    setShowEditModal(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async () => {
    if (!selectedUser) return;

    try {
      setFormLoading(true);
      
      const response = await apiService.updateUser(selectedUser.id, formData);
      
      if (response.success) {
        toast.success('User updated successfully');
        setShowEditModal(false);
        loadUsers();
      } else {
        toast.error(response.message || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      const response = await apiService.deleteUser(selectedUser.id);
      
      if (response.success) {
        toast.success('User deleted successfully');
        setShowDeleteModal(false);
        loadUsers();
      } else {
        toast.error(response.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreateAdminChange = (field, value) => {
    setCreateAdminData(prev => {
      const updatedData = { ...prev, [field]: value };
      
      // Clear farms selection when role changes to anything other than farms_owner
      if (field === 'role' && value !== 'farms_owner') {
        updatedData.farms = [];
      }
      
      return updatedData;
    });
  };

  const handleCreateAdmin = async () => {
    if (!createAdminData.name.trim() || !createAdminData.email.trim() || !createAdminData.password.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setFormLoading(true);
      const response = await apiService.createAdmin(createAdminData);
      
      if (response.success) {
        toast.success('Admin created successfully');
        setShowCreateAdminModal(false);
        setCreateAdminData({
          name: '',
          email: '',
          password: '',
          role: 'admin',
          farms: [],
        });
        loadUsers();
      } else {
        toast.error(response.message || 'Failed to create admin');
      }
    } catch (error) {
      console.error('Error creating admin:', error);
      toast.error('Failed to create admin');
    } finally {
      setFormLoading(false);
    }
  };

  const handleFarmSelection = (farmId) => {
    setCreateAdminData(prev => {
      const currentFarms = prev.farms || [];
      const isSelected = currentFarms.includes(farmId);
      
      if (isSelected) {
        return {
          ...prev,
          farms: currentFarms.filter(id => id !== farmId)
        };
      } else {
        return {
          ...prev,
          farms: [...currentFarms, farmId]
        };
      }
    });
  };

  const canCreateAdmin = currentUser && (currentUser.role === 'admin' || currentUser.role === 'super_admin');

  return (
    <div className="space-y-4 md:space-y-6 w-full max-w-full overflow-x-hidden">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">Users</h1>
          <p className="text-sm md:text-base text-secondary-600">Manage system users and their permissions</p>
        </div>
        {canCreateAdmin && (
          <button
            onClick={() => setShowCreateAdminModal(true)}
            className="btn btn-primary flex items-center gap-2 w-full md:w-auto"
          >
            <Plus className="w-4 h-4" />
            <span className="md:inline">Create Admin</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mx-2 md:mx-0">
        {/* Filter Header */}
        <div className="flex items-center justify-between p-3 md:p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 md:w-5 md:h-5 text-secondary-500" />
            <h3 className="text-base md:text-lg font-medium text-secondary-900">Filters</h3>
          </div>
          <button
            onClick={() => setIsFiltersCollapsed(!isFiltersCollapsed)}
            className="flex items-center space-x-1 text-secondary-600 hover:text-secondary-900 transition-colors p-1"
          >
            <span className="text-xs md:text-sm">{isFiltersCollapsed ? 'Show' : 'Hide'}</span>
            {isFiltersCollapsed ? (
              <ChevronDown className="w-3 h-3 md:w-4 md:h-4" />
            ) : (
              <ChevronUp className="w-3 h-3 md:w-4 md:h-4" />
            )}
          </button>
        </div>

        {/* Collapsible Filter Content */}
        {!isFiltersCollapsed && (
          <div className="p-3 md:p-6">
            <div className="space-y-3 md:space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 md:w-5 md:h-5 text-secondary-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="form-input pl-8 md:pl-10 w-full text-sm md:text-base"
                />
              </div>

              {/* Role and Status Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select
                  value={filters.role}
                  onChange={(e) => handleFilterChange('role', e.target.value)}
                  className="form-select w-full text-sm md:text-base"
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="researcher">Researcher</option>
                  <option value="observer">Observer</option>
                </select>

                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="form-select w-full text-sm md:text-base"
                >
                  <option value="">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-center md:justify-end">
                <button
                  onClick={() => setFilters({ search: '', role: '', status: '', page: 1, limit: 20 })}
                  className="btn btn-secondary text-sm md:text-base px-4 py-2"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Users Table - Desktop */}
      <div className="hidden md:block">
        <Table
          columns={columns}
          data={users}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onSort={handleSort}
          sortConfig={sortConfig}
          emptyMessage="No users found"
        />
      </div>

      {/* Users Cards - Mobile */}
      <div className="md:hidden space-y-3 px-2">
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow p-4 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : users.length > 0 ? (
          users.map((user) => {
            const roleColors = {
              admin: 'danger',
              researcher: 'warning',
              observer: 'info',
            };
            return (
              <div key={user.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 w-full max-w-full overflow-hidden">
                {/* Header */}
                <div className="flex items-start justify-between mb-3 w-full">
                  <div className="flex items-center space-x-2 flex-1 min-w-0 overflow-hidden">
                    <img
                      className="w-8 h-8 rounded-full flex-shrink-0"
                      src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=339f68&color=fff`}
                      alt={user.name}
                    />
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <div className="font-medium text-gray-900 text-xs truncate">
                        {user.name}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 flex-shrink-0">
                    <ActionButton
                      icon={Eye}
                      label="View Details"
                      onClick={() => handleViewUser(user)}
                      size="sm"
                      className="p-1"
                    />
                    <ActionMenu
                      item={user}
                      actions={[
                        {
                          key: 'edit',
                          label: 'Edit User',
                          icon: Edit,
                          onClick: handleEditUser,
                        },
                        {
                          key: 'delete',
                          label: 'Delete User',
                          icon: Trash2,
                          variant: 'danger',
                          onClick: handleDeleteUser,
                        },
                      ]}
                    />
                  </div>
                </div>

                {/* Info Grid */}
                <div className="grid grid-cols-2 gap-2 text-xs w-full">
                  <div className="min-w-0 overflow-hidden">
                    <div className="text-gray-500 text-xs uppercase tracking-wide">Role</div>
                    <div className="flex items-center mt-1">
                      <StatusBadge
                        status={user.role}
                        variant={roleColors[user.role] || 'default'}
                      />
                    </div>
                  </div>
                  <div className="min-w-0 overflow-hidden">
                    <div className="text-gray-500 text-xs uppercase tracking-wide">Last Login</div>
                    <div className="font-medium text-gray-900 text-xs truncate">
                      {(() => {
                        if (!user.last_login_at) return 'Never';
                        const date = new Date(user.last_login_at);
                        const now = new Date();
                        const diffTime = Math.abs(now - date);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays === 1) return 'Today';
                        if (diffDays <= 7) return `${diffDays}d ago`;
                        return date.toLocaleDateString();
                      })()}
                    </div>
                  </div>
                </div>

                {/* Created Date */}
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-500">
                    Created: {new Date(user.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="bg-white rounded-lg shadow p-6 text-center mx-2">
            <User className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No users found</p>
          </div>
        )}
      </div>

      {/* View User Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="User Details"
        size="lg"
      >
        {selectedUser && (
          <div className="space-y-6">
            <div className="flex items-center space-x-4">
              <img
                className="w-16 h-16 rounded-full"
                src={selectedUser.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(selectedUser.name)}&background=339f68&color=fff`}
                alt={selectedUser.name}
              />
              <div>
                <h3 className="text-lg font-semibold">{selectedUser.name}</h3>
                <p className="text-secondary-600">{selectedUser.email}</p>
                <StatusBadge status={selectedUser.role} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700">User ID</label>
                <p className="mt-1 text-sm text-secondary-900">{selectedUser.id}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Role</label>
                <p className="mt-1 text-sm text-secondary-900 capitalize">{selectedUser.role}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Created</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {new Date(selectedUser.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-secondary-700">Last Login</label>
                <p className="mt-1 text-sm text-secondary-900">
                  {selectedUser.last_login_at 
                    ? new Date(selectedUser.last_login_at).toLocaleString()
                    : 'Never'
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Edit User Modal */}
      <FormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={handleFormSubmit}
        title="Edit User"
        loading={formLoading}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="form-input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="form-input"
              required
              disabled
            />
            <p className="mt-1 text-sm text-secondary-500">Email cannot be changed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Role
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleInputChange('role', e.target.value)}
              className="form-select"
              required
            >
              <option value="observer">Observer</option>
              <option value="researcher">Researcher</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      </FormModal>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete User"
        message={`Are you sure you want to delete "${selectedUser?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      {/* Create Admin Modal */}
      <FormModal
        isOpen={showCreateAdminModal}
        onClose={() => setShowCreateAdminModal(false)}
        onSubmit={handleCreateAdmin}
        title="Create Admin"
        loading={formLoading}
        size="lg"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={createAdminData.name}
              onChange={(e) => handleCreateAdminChange('name', e.target.value)}
              className="form-input"
              placeholder="Enter admin name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={createAdminData.email}
              onChange={(e) => handleCreateAdminChange('email', e.target.value)}
              className="form-input"
              placeholder="Enter admin email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Password *
            </label>
            <input
              type="password"
              value={createAdminData.password}
              onChange={(e) => handleCreateAdminChange('password', e.target.value)}
              className="form-input"
              placeholder="Enter admin password"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              Role *
            </label>
            <select
              value={createAdminData.role}
              onChange={(e) => handleCreateAdminChange('role', e.target.value)}
              className="form-select"
              required
            >
              <option value="admin">Admin</option>
              {currentUser?.role === 'super_admin' && (
                <option value="super_admin">Super Admin</option>
              )}
              <option value="farms_owner">Farms Owner</option>
            </select>
          </div>

          {createAdminData.role === 'farms_owner' && (
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                Assign Farms
              </label>
              <div className="max-h-48 overflow-y-auto border border-secondary-300 rounded-md p-3">
                {farms.length === 0 ? (
                  <p className="text-sm text-secondary-500">No farms available</p>
                ) : (
                  <div className="space-y-2">
                    {farms.map((farm) => (
                      <label key={farm.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={createAdminData.farms.includes(farm.id)}
                          onChange={() => handleFarmSelection(farm.id)}
                          className="rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="ml-2 text-sm text-secondary-700">
                          {farm.name} ({farm.location})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="mt-1 text-sm text-secondary-500">
                Select farms this Farms Owner can manage
              </p>
            </div>
          )}
        </div>
      </FormModal>
    </div>
  );
};

export default UsersPage;